import type { CSSProperties } from 'react';
import { cn } from '../lib/cn.ts';

export interface SkeletonProps {
  className?: string;
  style?: CSSProperties;
  /** Fully rounded (avatars, dots). */
  round?: boolean;
}

/** A shimmering placeholder in the shape of the content that will load. Never a blank region. */
export function Skeleton({ className, style, round }: SkeletonProps) {
  return (
    <span
      aria-hidden
      style={{
        backgroundImage: 'linear-gradient(90deg, var(--surface-hover) 0%, var(--surface-active) 50%, var(--surface-hover) 100%)',
        backgroundSize: '220% 100%',
        ...style,
      }}
      className={cn('block animate-shimmer motion-reduce:animate-none', round ? 'rounded-full' : 'rounded-control', className)}
    />
  );
}

/** Lines of text-shaped skeletons; the last line is shorter. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span className={cn('flex flex-col gap-2', className)} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className="h-3" style={{ width: i === lines - 1 ? '60%' : `${92 - i * 6}%` }} />
      ))}
    </span>
  );
}

/** A list of row skeletons: the default loading state for any list. */
export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col', className)} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex h-11 items-center gap-3 border-b border-border-subtle px-4 last:border-b-0">
          <Skeleton round className="size-2" />
          <Skeleton className="h-3" style={{ width: `${38 + ((i * 17) % 30)}%` }} />
          <Skeleton className="ml-auto h-3 w-16" />
        </div>
      ))}
    </div>
  );
}
